import { MC_COLORS, isHexColor, isNamedColor, normalizeHex } from './colors';
import { CLICK_EVENT_FIELDS, isClickAction } from './compile';
import type { ClickEvent, PreviewSpan } from './types';

/**
 * Any text component as the spans a player would see, for the chat preview.
 *
 * The preview has to show compiled output and imported commands alike, in
 * every dialect, so this reads components the way the game does rather than
 * the way the compiler writes them: a child inherits its parent's colour,
 * formatting, click and hover, but never its parent's text or children. (The
 * older MinecraftText component spreads the whole parent into each child,
 * which repeats the parent's text and can recurse forever; this does not.)
 * The first element of an array is the parent of the rest, as in game.
 *
 * Components only a server can fill in (a translation, selector, score,
 * keybind or NBT value) become placeholder spans with stand-in text, so the
 * preview can show them differently instead of dropping them.
 *
 * Pasted input can be anything, so the walk is capped at a depth of 32 and at
 * 5000 spans (tooltips included), and it never throws.
 */

const MAX_DEPTH = 32;
const MAX_SPANS = 5000;

const STYLE_FLAGS = ['bold', 'italic', 'underlined', 'strikethrough', 'obfuscated'] as const;

type Json = Record<string, unknown>;

interface Inherited {
  color: string;
  bold: boolean;
  italic: boolean;
  underlined: boolean;
  strikethrough: boolean;
  obfuscated: boolean;
  click: ClickEvent | null;
  hover: PreviewSpan[] | null;
}

const ROOT: Inherited = {
  color: '#ffffff',
  bold: false,
  italic: false,
  underlined: false,
  strikethrough: false,
  obfuscated: false,
  click: null,
  hover: null,
};

interface Walk {
  out: PreviewSpan[];
  /** Spans left in the shared budget. */
  budget: { spans: number };
  /** False inside a tooltip, where clicks and hovers do nothing. */
  events: boolean;
}

const isRecord = (value: unknown): value is Json =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isPrimitiveText = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

function readColor(value: unknown): string | undefined {
  if (isNamedColor(value)) return MC_COLORS[value];
  if (typeof value === 'string' && isHexColor(value)) return normalizeHex(value);
  return undefined;
}

function readFlag(value: unknown): boolean | undefined {
  if (value === true || value === 1 || value === 'true') return true;
  if (value === false || value === 0 || value === 'false') return false;
  return undefined;
}

/** The node's click from either spelling; the parent's when it sets none, null when it sets one the preview cannot show. */
function readClick(node: Json, inherited: ClickEvent | null): ClickEvent | null {
  const current = node.click_event;
  const legacy = node.clickEvent;
  if (current == null && legacy == null) return inherited;
  if (isRecord(current) && isClickAction(current.action)) {
    const value = current[CLICK_EVENT_FIELDS[current.action]] ?? current.value;
    if (typeof value === 'string') return { action: current.action, value };
  }
  if (isRecord(legacy) && isClickAction(legacy.action) && typeof legacy.value === 'string') {
    return { action: legacy.action, value: legacy.value };
  }
  return null;
}

/**
 * The tooltip text a node sets: undefined when it sets no hover, null when its
 * hover is not text (an item or entity), else the component to show.
 * `hover_event.text` is how the 25w02a snapshot spelled `value`.
 */
function readHoverText(node: Json): unknown {
  const current = node.hover_event;
  const legacy = node.hoverEvent;
  if (current == null && legacy == null) return undefined;
  if (isRecord(current) && current.action === 'show_text') {
    const text = current.value ?? current.text;
    if (text != null) return text;
  }
  if (isRecord(legacy) && legacy.action === 'show_text') {
    const text = legacy.contents ?? legacy.value;
    if (text != null) return text;
  }
  return null;
}

/** What a node shows on its own, before its children. */
function contentOf(node: Json): { text: string; placeholder: boolean } | null {
  const str = (value: unknown) => (isPrimitiveText(value) ? String(value) : '');
  const kind = typeof node.type === 'string' ? node.type : undefined;
  if (kind === 'text' || (kind === undefined && node.text !== undefined)) {
    return { text: str(node.text), placeholder: false };
  }
  if (kind === 'translatable' || (kind === undefined && node.translate !== undefined)) {
    return { text: typeof node.fallback === 'string' ? node.fallback : str(node.translate), placeholder: true };
  }
  if (kind === 'score' || (kind === undefined && node.score !== undefined)) {
    return { text: isRecord(node.score) ? str(node.score.name) : '', placeholder: true };
  }
  if (kind === 'selector' || (kind === undefined && node.selector !== undefined)) {
    return { text: str(node.selector), placeholder: true };
  }
  if (kind === 'keybind' || (kind === undefined && node.keybind !== undefined)) {
    return { text: str(node.keybind), placeholder: true };
  }
  if (kind === 'nbt' || (kind === undefined && node.nbt !== undefined)) {
    return { text: '[nbt]', placeholder: true };
  }
  return null;
}

function emit(text: string, style: Inherited, placeholder: boolean, walk: Walk): void {
  if (!text || walk.budget.spans <= 0) return;
  walk.budget.spans -= 1;
  walk.out.push({
    text,
    color: style.color,
    bold: style.bold,
    italic: style.italic,
    underlined: style.underlined,
    strikethrough: style.strikethrough,
    obfuscated: style.obfuscated,
    click: style.click,
    hover: style.hover,
    placeholder,
  });
}

/** Emits the spans for `node` and returns the style it establishes, which array siblings inherit. */
function flattenInto(node: unknown, parent: Inherited, walk: Walk, depth: number): Inherited {
  if (depth > MAX_DEPTH || walk.budget.spans <= 0) return parent;
  if (isPrimitiveText(node)) {
    emit(String(node), parent, false, walk);
    return parent;
  }
  if (Array.isArray(node)) {
    if (node.length === 0) return parent;
    const first = flattenInto(node[0], parent, walk, depth + 1);
    for (let i = 1; i < node.length && walk.budget.spans > 0; i += 1) flattenInto(node[i], first, walk, depth + 1);
    return first;
  }
  if (!isRecord(node)) return parent;

  const style: Inherited = {
    ...parent,
    color: readColor(node.color) ?? parent.color,
    click: walk.events ? readClick(node, parent.click) : null,
    hover: walk.events ? hoverSpans(node, parent.hover, walk, depth) : null,
  };
  for (const flag of STYLE_FLAGS) style[flag] = readFlag(node[flag]) ?? parent[flag];

  const content = contentOf(node);
  if (content) emit(content.text, style, content.placeholder, walk);
  if (Array.isArray(node.extra)) {
    for (const child of node.extra) {
      if (walk.budget.spans <= 0) break;
      flattenInto(child, style, walk, depth + 1);
    }
  }
  return style;
}

/** The node's tooltip as spans (shared by every child that inherits it), or the parent's when it sets none. */
function hoverSpans(node: Json, inherited: PreviewSpan[] | null, walk: Walk, depth: number): PreviewSpan[] | null {
  const text = readHoverText(node);
  if (text === undefined) return inherited;
  if (text === null) return null;
  const tooltip: Walk = { out: [], budget: walk.budget, events: false };
  flattenInto(text, ROOT, tooltip, depth + 1);
  return tooltip.out.length > 0 ? tooltip.out : null;
}

/** The spans a player would see for any text component (object, array or string). */
export function flattenComponent(component: unknown): PreviewSpan[] {
  const walk: Walk = { out: [], budget: { spans: MAX_SPANS }, events: true };
  flattenInto(component, ROOT, walk, 0);
  return walk.out;
}
